import React, { useState, useContext, useEffect, useCallback, useMemo, Fragment } from 'react';

import Skeleton from 'ati-ui-react/components/Skeleton';
import { Invisible } from '@frontend/morpheus';

import styles from './styles.modules.scss';
import i18nStrings from './strings';
import {entities, externalOriginQuery, urlParams} from 'logic/entities';
import getArrayFromBitsum from 'utils/getArrayFromBitsum';
import { useDebounce } from 'utils/useDebounce';
import { useLocalized } from 'utils/useLocalized';
import { getCorrectOrderInRange } from 'utils/range';
import getUrlParameter from 'ati-utils/getUrlParameter';
import { DataContext, LoadingContext } from 'contexts';
import { defaultSearchParams } from 'logic/requestParams';
import DirectionAutosuggest from './DirectionAutosuggest';
import SwapButton from './SwapButton';
import PeriodSlider from './PeriodSlider';
import RangeNumbersInputs from './RangeNumbersInputs';
import TruckTypesSelector from './TruckTypesSelector';
import SearchPanel from './SearchPanel';
import Instruction from './Instruction';
import DirectionsLink from './DirectionsLink';
import { Country, GeoType } from 'consts/consts';
import { classes } from 'as-ui-kit/utils/styles';
import Coefficients from './Coefficients';
import {useNavigate} from 'react-router-dom';

const SearchFilter = () => {
    const strings = useLocalized(i18nStrings);
    let navigate = useNavigate();
    const { searchTruckers, findGeoById, canUseQueryParams, setCanUseQueryParams, setIsOutsideRussia, isOutsideRussia, carTypesArray, licenseInfo, isMobile, loadingFinished, writeTransitionSource, ratingParams, loadRatingParams, saveRatingParams, showPayByCardStatus } = useContext(DataContext);
    const {carTypesIsLoading} = useContext(LoadingContext);
    const [searchParams, setSearchParams] = useState(defaultSearchParams);
    const [selectedCarTypes, setSelectedCarTypes] = useState([]);
    const [carTypesDisplayNumber, setCarTypesDisplayNumber] = useState(0);
    const [error, setError] = useState(false);
    const [runForceSearch, setRunForceSearch] = useState(false);
    const [directionsNames, setDirectionsNames] = useState(null);
    const [utmParams, setUtmParams] = useState({});
    const [searchCoef, setSearchCoef] = useState(null);
    const [coefficientsEnabled, setCoefficientsEnabled] = useState(false);

    const getFilterQueryParams = useCallback((filter) => {
        let result = '';
        Object.keys(urlParams).forEach((key) => {
            if (filter[key] || filter[key] === 0) {
                const str = `${urlParams[key]}=${filter[key]}`;
                result = `${result}${result ? '&' : ''}${str}`;
            }
        });
        Object.keys(utmParams).forEach((key) => {
            const str = `${key}=${utmParams[key]}`;
            result = `${result}${result ? '&' : ''}${str}`;
        });

        if (getUrlParameter(externalOriginQuery)) {
            result = `${result}${result ? '&' : ''}${externalOriginQuery}=true`;
        }

        return result;
    }, [utmParams]);

    const setParamsToUrl = useCallback((filter) => {
        const queryParamsString = getFilterQueryParams(filter);
        if (queryParamsString) {
            navigate({search: queryParamsString});
        }
    }, [getFilterQueryParams, navigate]);

    const searchDebounce = useDebounce(150, useCallback((searchParams) => searchTruckers(searchParams), [searchTruckers]));

    const handleSearchClick = useCallback(() =>  {
        setRunForceSearch(false);
        if (!searchParams.idFrom || !searchParams.idTo){
            setError(true);
        } else {
            setParamsToUrl(searchParams);
            const result = {...searchParams};
            result.truckWeight = {
                from: searchParams.truckWeightFrom,
                to: searchParams.truckWeightTo
            };
            result.truckVolume = {
                from: searchParams.truckVolumeFrom,
                to: searchParams.truckVolumeTo
            };
            result.from = {
                id: searchParams.idFrom,
                type: searchParams.typeFrom
            };
            result.to = {
                id: searchParams.idTo,
                type: searchParams.typeTo
            };
            delete result.truckWeightFrom;
            delete result.truckWeightTo;
            delete result.truckVolumeFrom;
            delete result.truckVolumeTo;
            delete result.idFrom;
            delete result.typeFrom;
            delete result.idTo;
            delete result.typeTo;

            result.ratingCoefficients = searchCoef || null;
            searchDebounce(result);
        }
    }, [searchDebounce, setParamsToUrl, searchParams, searchCoef]);

    const handleChangeGeoFrom = useCallback((name, id, type, countryId) => {
        if (countryId && countryId !== Country.russia)
            setIsOutsideRussia(true);
        else if (searchParams.countryIdTo === Country.russia)
            setIsOutsideRussia(false);
        setSearchParams(prevState => ({
            ...prevState,
            geoFromName: name,
            idFrom: parseInt(id, 10),
            typeFrom: type,
            countryIdFrom: countryId
        }));
    },[searchParams, setIsOutsideRussia]);

    const handleChangeGeoTo = useCallback((name, id, type, countryId) => {
        if (countryId && countryId !== Country.russia)
            setIsOutsideRussia(true);
        else if (searchParams.countryIdFrom === Country.russia)
            setIsOutsideRussia(false);
        setSearchParams(prevState => ({
            ...prevState,
            geoToName: name,
            idTo: parseInt(id, 10),
            typeTo: type,
            countryIdTo: countryId
        }));
    },[searchParams, setIsOutsideRussia]);

    const swapRoutes = useCallback(() =>  {
        setSearchParams(prevState => ({
            ...prevState,
            idFrom: prevState.idTo,
            typeFrom: prevState.typeTo,
            geoFromName: prevState.geoToName,
            idTo: prevState.idFrom,
            typeTo: prevState.typeFrom,
            geoToName: prevState.geoFromName,
            countryIdFrom: prevState.countryIdTo,
            countryIdTo: prevState.countryIdFrom
        }));
    }, []);

    const handleSaveRatingParams = useCallback(() => {
        let rq = {};
        rq.from = {
            id: searchParams.idFrom,
            type: searchParams.typeFrom
        };
        rq.to = {
            id: searchParams.idTo,
            type: searchParams.typeTo
        };

        rq.ratingCoefficients = searchCoef || null;
        saveRatingParams(rq);
    }, [searchParams, searchCoef, saveRatingParams]);

    const handleChangePeriod = useCallback((beginDateStr, period, title) => {
        setSearchParams(prevState => ({
            ...prevState,
            beginDate: beginDateStr,
            period: period,
            periodTitle: title
        }));
    }, []);

    const handleChangeTruckWeightFrom = useCallback((weight) => {
        setSearchParams(prevState => {
            const truckWeight = getCorrectOrderInRange(parseInt(weight, 10) || null, prevState.truckWeightTo);
            return {
                ...prevState,
                truckWeightFrom: truckWeight?.from,
                truckWeightTo: truckWeight?.to
            };
        });
    }, []);

    const handleChangeTruckWeightTo = useCallback((weight) => {
        setSearchParams(prevState => {
            const truckWeight = getCorrectOrderInRange(prevState.truckWeightFrom, parseInt(weight, 10) || null);
            return {
                ...prevState,
                truckWeightFrom: truckWeight?.from,
                truckWeightTo: truckWeight?.to
            };
        });
    }, []);

    const handleChangeTruckVolumeFrom = useCallback((volume) => {
        setSearchParams(prevState => {
            const truckVolume = getCorrectOrderInRange(parseInt(volume, 10) || null, prevState.truckVolumeTo);
            return {
                ...prevState,
                truckVolumeFrom: truckVolume?.from,
                truckVolumeTo: truckVolume?.to
            };
        });
    }, []);

    const handleChangeTruckVolumeTo = useCallback((volume) => {
        setSearchParams(prevState => {
            const truckVolume = getCorrectOrderInRange(prevState.truckVolumeFrom, parseInt(volume, 10) || null);
            return {
                ...prevState,
                truckVolumeFrom: truckVolume?.from,
                truckVolumeTo: truckVolume?.to
            };
        });
    }, []);

    const handleChangeCarTypes = useCallback((result, bitsum, displayNumber) => {
        setSelectedCarTypes(result);
        setCarTypesDisplayNumber(displayNumber);
        setSearchParams(prevState => ({
            ...prevState,
            truckType: bitsum == 0 ? null : +bitsum
        }));
    }, []);

    const handleResetCarTypes = useCallback(() => {
        setSelectedCarTypes([]);
        setCarTypesDisplayNumber(0);
        setSearchParams(prevState => ({
            ...prevState,
            truckType: null
        }));
    }, []);

    const sliderIsLocked = useMemo(() => !licenseInfo.searchAccessFinishUtc && !isOutsideRussia, [licenseInfo, isOutsideRussia]);

    const fillFromToByGeoSearch = useCallback((geoPoints, idFrom, typeFrom, idTo, typeTo) => {
        const geoFrom = geoPoints.find((p) => p.id === idFrom && p.type === typeFrom);
        const geoTo = geoPoints.find((p) => p.id === idTo && p.type === typeTo);
        if (geoFrom) {
            handleChangeGeoFrom(geoFrom.name, idFrom, geoFrom.type, geoFrom.countryId);
        }

        if (geoTo) {
            handleChangeGeoTo(geoTo.name, idTo, geoTo.type, geoTo.countryId);
        }

        setIsOutsideRussia(geoFrom.countryId !== Country.russia || geoTo.countryId  !== Country.russia);
    }, [handleChangeGeoFrom, handleChangeGeoTo, setIsOutsideRussia]);

    const searchPanelJSX = useMemo(() => (
        <SearchPanel
            handleSearchClick={handleSearchClick}
        />
    ), [handleSearchClick]);

    const setUtmParamsFromUrl = () => {
        const utmUrlParams = {};
        const urlSearchParams = new URLSearchParams(window.location.search);
        for (let key of urlSearchParams.keys()) {
            if (key.includes('utm'))
                utmUrlParams[key] = urlSearchParams.get(key);
        }
        setUtmParams(utmUrlParams);
    };

    const setParamsFromUrl = useCallback(() => {
        setCanUseQueryParams(prevState => ({
            ...prevState,
            used: true
        }));

        setUtmParamsFromUrl();

        const transitionSource = getUrlParameter(urlParams.transitionSource);
        if (transitionSource) {
            writeTransitionSource(transitionSource);
        }
        const payByCardStatus = getUrlParameter(urlParams.payByBankCardStatus);
        if (payByCardStatus) {
            showPayByCardStatus(payByCardStatus);
        }

        let cityFrom = Number(getUrlParameter(urlParams.cityFrom));
        let cityTo = Number(getUrlParameter(urlParams.cityTo));
        let idFrom = Number(getUrlParameter(urlParams.idFrom));
        let typeFrom = Number(getUrlParameter(urlParams.typeFrom));
        let idTo = Number(getUrlParameter(urlParams.idTo));
        let typeTo = Number(getUrlParameter(urlParams.typeTo));
        const carType = getUrlParameter(urlParams.truckType);
        const truckWeightFrom = getUrlParameter(urlParams.truckWeightFrom);
        const truckWeightTo = getUrlParameter(urlParams.truckWeightTo);
        const truckVolumeFrom = getUrlParameter(urlParams.truckVolumeFrom);
        const truckVolumeTo = getUrlParameter(urlParams.truckVolumeTo);

        if (carType) {
            const types = getArrayFromBitsum(carType, carTypesArray);
            if (types) {
                handleChangeCarTypes(types.values, carType, types.checkedNumber);
            }
        }
        if (parseInt(truckWeightFrom, 10)) {
            handleChangeTruckWeightFrom(truckWeightFrom);
        }
        if (parseInt(truckWeightTo, 10)) {
            handleChangeTruckWeightTo(truckWeightTo);
        }
        if (parseInt(truckVolumeFrom, 10)) {
            handleChangeTruckVolumeFrom(truckVolumeFrom);
        }
        if (parseInt(truckVolumeTo, 10)) {
            handleChangeTruckVolumeTo(truckVolumeTo);
        }
        if (!idFrom) {
            if (cityFrom) {
                idFrom = cityFrom;
                typeFrom = GeoType.city;
            } else {
                idFrom = 1100; //минск
                typeFrom = GeoType.city;
            }
        }

        if (!idTo) {
            if (cityTo) {
                idTo = cityTo;
                typeTo = GeoType.city;
            } else {
                idTo = 3611; //москва
                typeTo = GeoType.city;
            }
        }

        findGeoById([{id: idFrom, type: typeFrom}, {id: idTo, type: typeTo}]).then((geoPoints) => {
            fillFromToByGeoSearch(geoPoints, idFrom, typeFrom, idTo, typeTo);
            setRunForceSearch(true);
            loadingFinished(entities.filters);
        });
    }, [findGeoById, carTypesArray, handleChangeCarTypes, handleChangeTruckWeightFrom, handleChangeTruckWeightTo,
        handleChangeTruckVolumeFrom, handleChangeTruckVolumeTo, fillFromToByGeoSearch, loadingFinished, setCanUseQueryParams, writeTransitionSource, showPayByCardStatus]);

    useEffect(() => {
        if (canUseQueryParams && !canUseQueryParams.used && canUseQueryParams[entities.license] && canUseQueryParams[entities.carTypes]) {
            setParamsFromUrl();
        }
        if (error) {
            setError(!searchParams.idFrom || !searchParams.idTo);
        }
        if (runForceSearch) {
            handleSearchClick();
        }
    }, [canUseQueryParams, setParamsFromUrl, searchParams, error, runForceSearch, handleSearchClick]);

    useEffect(() => {
        if (!coefficientsEnabled || !searchParams.idFrom || !searchParams.idTo)
            return;

        let rq = {};
        rq.from = {
            id: searchParams.idFrom,
            type: searchParams.typeFrom
        };
        rq.to = {
            id: searchParams.idTo,
            type: searchParams.typeTo
        };
        loadRatingParams(rq);
    }, [coefficientsEnabled, searchParams.idFrom, searchParams.idTo, searchParams.typeFrom, searchParams.typeTo, loadRatingParams]);

    const outsideDirections = useMemo(() => [
        {
            cityIdFrom: 1100,
            cityIdTo: 1013,
        },
        {
            cityIdFrom: 2197,
            cityIdTo: 3611,
        },
        {
            cityIdFrom: 270,
            cityIdTo: 1004,
        }
    ], []);

    useEffect(() => {
        const cities = [];
        for (const d of outsideDirections) {
            cities.push({id: d.cityIdFrom, type: 2});
            cities.push({id: d.cityIdTo, type: 2});
        }

        findGeoById(cities).then((names) => setDirectionsNames(names));
    }, [findGeoById, outsideDirections]);

    const handleDirectionsClick = (idFrom, typeFrom, idTo, typeTo) => fillFromToByGeoSearch(directionsNames, idFrom, typeFrom, idTo, typeTo);

    const coefficientsFeatureJSX = useMemo(() => {
        return (
            <Invisible
                name="at_coefficients"
                off={() => { setCoefficientsEnabled(false); return (<Fragment/>);}}
                on={() => { setCoefficientsEnabled(true); return ratingParams && (
                    <Coefficients
                        saveRatingParams={handleSaveRatingParams}
                        searchCoef={searchCoef}
                        setSearchCoef={setSearchCoef}
                    />
                );}}
            />
        );
    }, [ratingParams, searchCoef, setSearchCoef, handleSaveRatingParams]);

    return (
        <Fragment>
            {coefficientsFeatureJSX}
            <div className={styles['filter-container']}>
                <div className={styles['filter']}>
                    <div className={styles['bordered-box']}>
                        <div className={classes(styles['row'], styles['inputs'])}>
                            <DirectionAutosuggest
                                error={error && !searchParams.idFrom}
                                title={strings.cityFrom}
                                value={searchParams.geoFromName}
                                onChange={handleChangeGeoFrom}
                            />
                            <div className={styles['swap-button']}>
                                <SwapButton
                                    onClick={swapRoutes}
                                />
                            </div>
                            <DirectionAutosuggest
                                error={error && !searchParams.idTo}
                                title={strings.cityTo}
                                value={searchParams.geoToName}
                                onChange={handleChangeGeoTo}
                            />
                        </div>

                        <div className={classes(styles['row'], styles['slider'])}>
                            <PeriodSlider
                                locked={sliderIsLocked}
                                onChange={handleChangePeriod}
                            />
                        </div>
                        {directionsNames && (
                            <div className={styles['free-directions']}>
                                <b>
                                    {strings.free}
                                </b>
                                {strings.outsideRussia}
                                <span className={styles['directions-label-in-row']}>
                                    {strings.try}
                                </span>
                                <div className={styles['directions']}>
                                    <div className={styles['directions-label']}>
                                        {strings.try}
                                    </div>
                                    {outsideDirections.map((d, index) => (
                                        <DirectionsLink
                                            idFrom={d.cityIdFrom}
                                            idTo={d.cityIdTo}
                                            key={index.toString()}
                                            nameFrom={directionsNames.find(dn => dn.id === d.cityIdFrom).name}
                                            nameTo={directionsNames.find(dn => dn.id === d.cityIdTo).name}
                                            onClick={handleDirectionsClick}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles['box']}>
                        <div className={styles['row']}>
                            <RangeNumbersInputs
                                after={(
                                    <span className={styles['after-input']}>
                                        {strings.tons}
                                    </span>
                                )}
                                title={strings.carryingCapacity}
                                valueFrom={searchParams.truckWeightFrom}
                                valueTo={searchParams.truckWeightTo}
                                onChangeFrom={handleChangeTruckWeightFrom}
                                onChangeTo={handleChangeTruckWeightTo}
                            />
                        </div>
                        <div className={styles['row']}>
                            <RangeNumbersInputs
                                after={(
                                    <span className={styles['after-input-with-sup']}>
                                        {strings.meters}
                                        <sup>
                                            {'3'}
                                        </sup>
                                    </span>
                                )}
                                title={strings.bodyVolume}
                                valueFrom={searchParams.truckVolumeFrom}
                                valueTo={searchParams.truckVolumeTo}
                                onChangeFrom={handleChangeTruckVolumeFrom}
                                onChangeTo={handleChangeTruckVolumeTo}
                            />
                        </div>
                    </div>

                    <div className={styles['truck-type-box']}>
                        {carTypesIsLoading ?
                            (
                                <Skeleton
                                    borderRadius={2}
                                    className={styles['skeleton']}
                                    height={150}
                                    width={isMobile ? '100%' : '147px'}
                                />
                            ) : (
                                <TruckTypesSelector
                                    displayNumber={carTypesDisplayNumber}
                                    resetChecked={handleResetCarTypes}
                                    selectedValues={selectedCarTypes}
                                    title={strings.carTypes}
                                    onChange={handleChangeCarTypes}
                                />
                            )
                        }
                    </div>
                </div>
                <div className={styles['bottom-elements']}>
                    {!isMobile ? <Instruction/> : null}
                    {searchPanelJSX}
                </div>
            </div>

            {isMobile ? <Instruction/> : null}
        </Fragment>
    );
};

export default SearchFilter;
